import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { ERROR_PASSWORDS_DONT_MATCH, SUCCESS_PASSWORD_CHANGE } from '@/utils/messages';
import { toast_error, toast_success } from '@/libs/toast-wrappers';
import { changePassword, resetPassword } from '@/api/auth';
import { ROUTES } from '@/router/routes';
import { emailSchema, passwordSchema } from '@/utils/zod-schemas';

import { InputField } from '../form/input-field';
import { Button } from '../ui/button';

const schema = z
  .object({
    email: emailSchema,
    currentPassword: z.string().optional(),
    newPassword: passwordSchema,
    confirmNewPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmNewPassword, {
    message: ERROR_PASSWORDS_DONT_MATCH,
    path: ['confirmNewPassword'],
  });

type ChangePassFormData = z.infer<typeof schema>;

export function ChangePass() {
  const { t } = useTranslation();
  const [isForgotPass, setIsForgotPass] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePassFormData>({ resolver: zodResolver(schema) });
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setIsForgotPass(true);
      setToken(token);
    }
  }, []);

  const onSubmitChangePass: SubmitHandler<ChangePassFormData> = async (data) => {
    try {
      setIsLoading(true);
      const { email, currentPassword, newPassword, confirmNewPassword } = data;

      if (isForgotPass) {
        await resetPassword(email, newPassword, confirmNewPassword, token);
        reset();
        toast_success(SUCCESS_PASSWORD_CHANGE);
        setTimeout(function () {
          void navigate(ROUTES.LOGIN);
        }, 5000);
        setIsLoading(false);
        return;
      }

      if (!currentPassword) {
        setIsLoading(false);
        toast_error(t('changepass-current-required'));
        return;
      }

      await changePassword(email, newPassword, confirmNewPassword, currentPassword);
      reset();
      toast_success(SUCCESS_PASSWORD_CHANGE);
      setTimeout(function () {
        void navigate(ROUTES.LOGIN);
      }, 2000);

      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <>
      <h1 className="title-auth mb-2">{t('changepass-title')}</h1>
      <form
        className="w-full my-12 flex flex-col gap-6"
        onSubmit={handleSubmit(onSubmitChangePass)}
      >
        <InputField
          disabled={isLoading}
          className="login-email-input"
          label={t('login-email')}
          type="email"
          required
          errorMessage={errors.email?.message}
          {...register('email', { required: true })}
        />
        {!isForgotPass && (
          <InputField
            disabled={isLoading}
            className="login-password-input"
            label={t('changepass-existing')}
            type="password"
            required
            errorMessage={errors.currentPassword?.message}
            {...register('currentPassword', { required: true })}
          />
        )}
        <InputField
          disabled={isLoading}
          className="login-password-input"
          label={t('changepass-new')}
          type="password"
          required
          errorMessage={errors.newPassword?.message}
          help={t('password-complexity-guide-label')}
          {...register('newPassword', { required: true })}
        />
        <InputField
          disabled={isLoading}
          className="login-password-input"
          label={t('changepass-confirm')}
          type="password"
          required
          errorMessage={errors.confirmNewPassword?.message}
          {...register('confirmNewPassword', { required: true })}
        />
        <div className="flex flex-wrap items-center gap-4">
          <Button type="submit" className="button" loading={isLoading}>
            {t('changepass-submit')}
          </Button>
          <div className="inline-flex gap-4 items-center">
            <span>{t('or')}</span>
            <Button href={ROUTES.LOGIN} variant="link">
              {t('changepass-login')}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
